import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

/**
 * Read-only tenant documents + signed contracts for a property the owner is
 * assigned to. Backed by the `owner_property_documents` SECURITY DEFINER RPC
 * (ownership verified server-side). Loaded lazily by the Documents tab.
 */

export type OwnerDocument = {
  id: string;
  title: string | null;
  document_type: string | null;
  file_url: string | null;
  file_name: string | null;
  status: string | null;
  created_at: string | null;
  room_title: string | null;
  tenant_name: string | null;
  booking_id: number | null;
  /**
   * Which area wrote the row: the tenant's own dashboard, the CoLiviq admin
   * area, or one of our generating edge functions. Null only for a response
   * from a database that predates the column, which the feed reads as 'tenant'.
   */
  source: "tenant" | "admin" | "system" | null;
};

export type OwnerPropertyDocuments = {
  documents: OwnerDocument[];
};

export function useOwnerPropertyDocuments(
  propertyId: number | null,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["owner-property-documents", propertyId],
    enabled: !!propertyId && enabled,
    queryFn: async (): Promise<OwnerPropertyDocuments | null> => {
      const { data, error } = await supabase.rpc("owner_property_documents", {
        _property_id: propertyId,
      });
      if (error) throw error;
      return data as OwnerPropertyDocuments | null;
    },
  });
}
